import React, { useState, useEffect, useRef } from 'react';
import DocViewer from './DocViewer';
import Modal from './Modal';
import CalendarPicker from './CalendarPicker';
import CustomSelect from './CustomSelect';
import CompanySelect from './CompanySelect';
import AutocompleteInput from './AutocompleteInput';
import { useFormData } from '../hooks/useFormData';
import { COST_TYPES } from '../utils/costTypes';

const getFileUrl = (filename) => {
  if (!filename) return null;
  const base = (process.env.REACT_APP_API_URL || 'http://localhost:4000').replace(/\/api\/?$/, '');
  return `${base}/uploads/costs/${filename}`;
};

const CostForm = ({ isOpen, onClose, cost, onSave, defaultPlate, defaultCompany, defaultVehicleName }) => {
  const [formData, setFormData] = useState({
    plate: '', date: '', costType: 'Fuel', amount: '', currency: 'RON',
    description: '', receiptRef: '', company: '', vehicleName: '',
  });
  const [files, setFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [loading, setLoading] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    setFiles([]);
    if (cost && cost._id) {
      setFormData({
        plate: cost.plate || '',
        date: cost.date ? cost.date.slice(0, 10) : '',
        costType: cost.costType || 'Fuel',
        amount: cost.amount ?? '',
        currency: cost.currency || 'RON',
        description: cost.description || '',
        receiptRef: cost.receiptRef || '',
        company: cost.company || '',
        vehicleName: cost.vehicleName || '',
      });
    } else {
      setFormData({
        plate: (cost && cost.plate) || defaultPlate || '',
        date: '',
        costType: 'Fuel',
        amount: '',
        currency: 'RON',
        description: '',
        receiptRef: '',
        company: (cost && cost.company) || defaultCompany || '',
        vehicleName: (cost && cost.vehicleName) || defaultVehicleName || '',
      });
    }
  }, [cost, isOpen, defaultPlate, defaultCompany, defaultVehicleName]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFilesSelect = (selected) => {
    const list = Array.from(selected || []);
    if (!list.length) return;
    const allowed = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    const valid = [];
    for (const f of list) {
      if (!allowed.includes(f.type)) {
        alert(`${f.name}: only PDF, JPG, and PNG files are allowed.`);
        continue;
      }
      if (f.size > 10 * 1024 * 1024) {
        alert(`${f.name}: file size must be under 10MB.`);
        continue;
      }
      valid.push(f);
    }
    setFiles((prev) => [...prev, ...valid]);
  };

  const handleRemoveFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFilesSelect(e.dataTransfer.files);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onSave({ ...formData, amount: Number(formData.amount) }, files.length ? files : undefined);
      onClose();
    } catch (err) {
      alert('Error saving cost. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const isEditing = !!(cost && cost._id);
  const existingAttachments = cost?.attachments?.length ? cost.attachments : (cost?.attachment?.filename ? [cost.attachment] : []);
  const fd = useFormData();

  const handlePlateSelect = (plate) => {
    const v = fd.getVehicleByPlate(plate);
    if (v) setFormData(prev => ({
      ...prev, plate,
      vehicleName: [v.brand, v.model].filter(Boolean).join(' ') || prev.vehicleName,
      company: v.company || prev.company,
    }));
  };

  return (
    <>
    <Modal isOpen={isOpen} onClose={onClose} title={isEditing ? 'Edit Cost' : 'Add Cost'} size="md">
      <form onSubmit={handleSubmit}>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Vehicle Plate *</label>
            <AutocompleteInput name="plate" value={formData.plate} onChange={handleChange}
              suggestions={fd.plates} onSelect={handlePlateSelect} required placeholder="e.g. B-12-XYZ" />
          </div>
          <div className="form-group">
            <label className="form-label">Vehicle (Brand / Model)</label>
            <AutocompleteInput name="vehicleName" value={formData.vehicleName} onChange={handleChange}
              suggestions={fd.vehicleNames} placeholder="e.g. Dacia Logan" />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Company</label>
            <CompanySelect name="company" value={formData.company} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label className="form-label">Date *</label>
            <CalendarPicker name="date" value={formData.date} onChange={handleChange} required />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Cost Type *</label>
            <CustomSelect name="costType" className="form-select" value={formData.costType} onChange={handleChange} required>
              {COST_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </CustomSelect>
          </div>
          <div className="form-group">
            <label className="form-label">Amount *</label>
            <input type="number" name="amount" className="form-input" value={formData.amount} onChange={handleChange} required placeholder="0.00" min="0" step="0.01" />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Currency</label>
            <CustomSelect name="currency" className="form-select" value={formData.currency} onChange={handleChange}>
              <option value="RON">RON</option>
              <option value="EUR">EUR</option>
            </CustomSelect>
          </div>
          <div className="form-group">
            <label className="form-label">Receipt / Reference</label>
            <input type="text" name="receiptRef" className="form-input" value={formData.receiptRef} onChange={handleChange} placeholder="e.g. INV-2024-001" />
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Description</label>
          <input type="text" name="description" className="form-input" value={formData.description} onChange={handleChange} placeholder="e.g. Full tank — 50L" />
        </div>

        {/* Document File Upload */}
        <div className="form-group" style={{ marginTop: '8px' }}>
          <label className="form-label">Document Files</label>

          {files.length > 0 && (
            <div style={{ display: 'grid', gap: '8px', marginBottom: '8px' }}>
              {files.map((f, idx) => (
                <div key={`${f.name}-${idx}`} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '10px' }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0077b6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                  </svg>
                  <span style={{ flex: 1, fontSize: '13px', color: '#0077b6', fontWeight: 600 }}>{f.name}</span>
                  <span style={{ fontSize: '12px', color: '#64748b' }}>{(f.size / 1024).toFixed(0)} KB</span>
                  <button type="button" onClick={() => handleRemoveFile(idx)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: '2px' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  </button>
                </div>
              ))}
            </div>
          )}

          <div
            onClick={() => fileInputRef.current?.click()}
            onDrop={handleDrop}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            style={{
              border: `2px dashed ${dragOver ? '#0077b6' : '#cbd5e1'}`,
              borderRadius: '10px',
              padding: '28px 20px',
              textAlign: 'center',
              cursor: 'pointer',
              background: dragOver ? '#f0f9ff' : '#f8fafc',
              transition: 'all 0.15s',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '10px' }}>
              <div style={{ width: '44px', height: '44px', background: '#e0f2fe', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#0077b6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="16 16 12 12 8 16" />
                  <line x1="12" y1="12" x2="12" y2="21" />
                  <path d="M20.39 18.39A5 5 0 0018 9h-1.26A8 8 0 103 16.3" />
                </svg>
              </div>
            </div>
            <div style={{ fontSize: '14px', fontWeight: 600, color: '#0f172a', marginBottom: '4px' }}>Click or drag files here</div>
            <div style={{ fontSize: '12px', color: '#94a3b8' }}>PDF, JPG, PNG — max. 10MB each, multiple allowed</div>
          </div>

          {/* Show existing attachments when editing */}
          {isEditing && existingAttachments.length > 0 && (
            <div style={{ marginTop: '8px', display: 'grid', gap: '6px' }}>
              {existingAttachments.map((att, idx) => (
                <div key={att.filename || idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#64748b' }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"/></svg>
                  Current:
                  <button onClick={() => setPreviewDoc({ url: getFileUrl(att.filename), filename: att.originalName })} style={{ background: 'none', border: 'none', padding: 0, color: '#0077b6', fontWeight: 500, cursor: 'pointer' }}>{att.originalName}</button>
                </div>
              ))}
              <span style={{ color: '#94a3b8', fontSize: 12 }}>(uploading new files replaces all current attachments)</span>
            </div>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            multiple
            style={{ display: 'none' }}
            onChange={(e) => { handleFilesSelect(e.target.files); e.target.value = ''; }}
          />
        </div>

        <div className="form-actions">
          <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? 'Saving...' : isEditing ? 'Update' : 'Add Cost'}</button>
        </div>
      </form>
    </Modal>
    <DocViewer url={previewDoc?.url} filename={previewDoc?.filename} onClose={() => setPreviewDoc(null)} />
    </>
  );
};

export default CostForm;
